import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  Award,
  BarChart2,
  CheckCircle,
  Clock,
  MessageSquare,
  Mic,
  Target,
} from "react-feather";
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../helpers/utils/Constants/route";
import { useAppDispatch } from "../../../store/store";
import {
  fetchAudio,
  fetchEvaluations,
  fetchFeedback,
  fetchInterviewDetails,
} from "../../../store/features/AdminPanel/InterviewLogDetail/InterviewLogDetailApi";
import { normalizeInterviewPercent } from "../InterviewLogs/utils/interviewLogCardUtils";
import { getInterviewScoreMeta } from "../InterviewLogs/utils/interviewScoreMeta";
import "./interviewLogDetailReportV2.scss";

type TabKey = "rubric" | "questions" | "transcript";

// Recharts types can conflict with this repo's React/TS versions in CRA builds (TS2786).
// Use `any`-typed aliases to keep runtime behavior while avoiding JSX typing issues.
const PolarAngleAxisC: any = PolarAngleAxis;
const PolarGridC: any = PolarGrid;
const PolarRadiusAxisC: any = PolarRadiusAxis;
const RadarC: any = Radar;
const RadarChartC: any = RadarChart;
const ResponsiveContainerC: any = ResponsiveContainer;
const TooltipC: any = Tooltip;

type Metric = {
  id: string;
  name: string;
  score: number;
  maxScore: number;
  percentScore: number;
  rating?: string;
  suggestion?: string;
  example?: string;
  description?: string;
};

const formatShortDate = (dateString: string | null | undefined) => {
  if (!dateString) return "—";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatTimestamp = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.max(0, totalSeconds % 60);
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const getTranscriptTimestampSeconds = (
  msg: any,
  baseFirstReceivedTime: number | null
): number | null => {
  const rawStartTime = Number(msg?.start_time);
  if (Number.isFinite(rawStartTime) && rawStartTime > 0) {
    return rawStartTime > 100000
      ? Math.round(rawStartTime / 1000)
      : Math.round(rawStartTime);
  }

  const rawFirstReceivedTime = Number(msg?.first_received_time);
  if (
    Number.isFinite(rawFirstReceivedTime) &&
    rawFirstReceivedTime > 0 &&
    baseFirstReceivedTime !== null
  ) {
    return Math.max(0, Math.round((rawFirstReceivedTime - baseFirstReceivedTime) / 1000));
  }

  return null;
};

const getScorePercentColor = (pct: number): string => {
  if (pct >= 70) return "#16a34a";
  if (pct >= 50) return "#d97706";
  return "#dc2626";
};

const RadarTooltip = ({ active, payload }: any) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0]?.payload;
  if (!data) return null;

  const pct = Math.round(Number(data.score) || 0);
  const color = getScorePercentColor(pct);

  return (
    <div className="irv2-radar-tooltip">
      <div className="irv2-radar-tooltip__label">{data.category}</div>
      <div className="irv2-radar-tooltip__score" style={{ color }}>
        {pct}
        <span className="irv2-radar-tooltip__denom">/100</span>
      </div>
    </div>
  );
};

/** Ring + text colors by score / max score (matches interview-prep CategoryScoresCard). */
const getCategoryRingColor = (score: number, maxScore: number): string => {
  const max = maxScore > 0 ? maxScore : 10;
  const pct = (score / max) * 100;
  if (pct >= 70) return "#10b981";
  if (pct >= 50) return "#f59e0b";
  return "#ef4444";
};

/** Shared with category cards + expanded feedback header score. */
const getScoreBandClass = (score: number, maxScore: number): string => {
  const max = maxScore > 0 ? maxScore : 10;
  const pct = (score / max) * 100;
  if (pct >= 70) return "irv2-scoreBand--strong";
  if (pct >= 50) return "irv2-scoreBand--improving";
  return "irv2-scoreBand--needs-work";
};

interface Props {
  interviewId: string;
}

const InterviewLogDetailReportV2: React.FC<Props> = ({ interviewId }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabKey>("rubric");
  const [interview, setInterview] = useState<any | null>(null);
  const [feedback, setFeedback] = useState<any | null>(null);
  const [evaluations, setEvaluations] = useState<any[]>([]);
  const [audioData, setAudioData] = useState<any | null>(null);

  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | number | null>(
    null
  );

  const categoryScoresRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const run = async () => {
      if (!interviewId) return;
      setLoading(true);
      try {
        const [i, f, e, a] = await Promise.allSettled([
          dispatch(fetchInterviewDetails(interviewId)),
          dispatch(fetchFeedback(interviewId)),
          dispatch(fetchEvaluations(interviewId)),
          dispatch(fetchAudio(interviewId)),
        ]);

        if (i.status === "fulfilled") {
          const payload: any = i.value?.payload;
          setInterview(payload?.data ?? payload ?? null);
        }

        if (f.status === "fulfilled") {
          const payload: any = f.value?.payload;
          setFeedback(payload?.data ?? payload ?? null);
        }

        if (e.status === "fulfilled") {
          const payload: any = e.value?.payload;
          const data = payload?.data ?? payload;
          setEvaluations(Array.isArray(data) ? data : []);
        }

        if (a.status === "fulfilled") {
          const payload: any = a.value?.payload;
          setAudioData(payload?.data ?? payload ?? null);
        }
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [dispatch, interviewId]);

  const jobRole = useMemo(() => {
    return interview?.role || interview?.interviewType || "Interview";
  }, [interview]);

  const interviewDate = useMemo(() => {
    return formatShortDate(interview?.createdAt || interview?.created_at || interview?.date);
  }, [interview]);

  const durationLabel = useMemo(() => {
    const raw = interview?.duration ?? feedback?.interviewDuration ?? "";
    return raw ? String(raw) : "—";
  }, [interview, feedback]);

  const overallScorePct = useMemo(() => {
    const raw =
      interview?.overallScore ??
      interview?.overall_score ??
      feedback?.overallScore ??
      feedback?.overall_score ??
      null;
    const n = normalizeInterviewPercent(raw);
    return n ?? 0;
  }, [interview, feedback]);

  const metrics: Metric[] = useMemo(() => {
    const rubric = feedback?.rubricFeedback;
    if (Array.isArray(rubric) && rubric.length > 0) {
      return rubric.map((item: any, idx: number) => {
        const score = Number(item?.score ?? 0);
        const maxScore = Number(item?.maxScore ?? 10);
        const percentScore = maxScore > 0 ? (score / maxScore) * 100 : 0;
        return {
          id: item?.id ?? `metric-${idx}`,
          name: item?.title ?? `Category ${idx + 1}`,
          score,
          maxScore,
          percentScore,
          rating: item?.status,
          suggestion: item?.suggestion,
          example: item?.example,
          description: item?.description,
        };
      });
    }

    // Fallback to top-level sub-scores if rubric feedback isn't present.
    const safe = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);
    const maybe = [
      { name: "Communication", score: safe(feedback?.communicationScore), maxScore: 10 },
      { name: "STAR Format", score: safe(feedback?.starFormatScore), maxScore: 10 },
      { name: "Technical Depth", score: safe(feedback?.technicalDepthScore), maxScore: 10 },
      { name: "Leadership", score: safe(feedback?.leadershipScore), maxScore: 10 },
    ];
    return maybe.map((m, idx) => ({
      id: `metric-fallback-${idx}`,
      name: m.name,
      score: m.score,
      maxScore: m.maxScore,
      percentScore: m.maxScore > 0 ? (m.score / m.maxScore) * 100 : 0,
    }));
  }, [feedback]);

  const bestArea = useMemo(() => {
    if (metrics.length === 0) return null;
    return metrics.reduce((best, m) => (m.percentScore > best.percentScore ? m : best), metrics[0]);
  }, [metrics]);

  const focusArea = useMemo(() => {
    if (metrics.length === 0) return null;
    return metrics.reduce((worst, m) => (m.percentScore < worst.percentScore ? m : worst), metrics[0]);
  }, [metrics]);

  useEffect(() => {
    if (metrics.length === 0) {
      if (expandedCategory !== null) setExpandedCategory(null);
      return;
    }

    const isExpandedValid =
      expandedCategory !== null && metrics.some((m) => m.name === expandedCategory);

    // Default-open the first category (also fixes cases where the previously expanded
    // category doesn't exist for a different interview).
    if (!isExpandedValid) {
      setExpandedCategory(metrics[0].name);
    }
  }, [expandedCategory, metrics]);

  useEffect(() => {
    if (evaluations.length > 0 && selectedQuestionId === null) {
      setSelectedQuestionId(evaluations[0]?.id ?? 0);
    }
  }, [evaluations, selectedQuestionId]);

  const selectedQuestionIndex = useMemo(() => {
    if (selectedQuestionId === null) return -1;
    return evaluations.findIndex((q) => (q?.id ?? "") === selectedQuestionId);
  }, [evaluations, selectedQuestionId]);

  const selectedQuestion = useMemo(() => {
    if (selectedQuestionIndex < 0) return null;
    return evaluations[selectedQuestionIndex] ?? null;
  }, [evaluations, selectedQuestionIndex]);

  const transcript = useMemo(() => {
    return (
      interview?.transcriptions ??
      interview?.transcription ??
      audioData?.transcriptions ??
      audioData?.transcription ??
      null
    );
  }, [audioData, interview]);

  const transcriptBaseFirstReceivedTime = useMemo(() => {
    if (!Array.isArray(transcript)) return null;
    const numericTimes = transcript
      .map((msg: any) => Number(msg?.first_received_time))
      .filter((value: number) => Number.isFinite(value) && value > 0);
    return numericTimes.length > 0 ? Math.min(...numericTimes) : null;
  }, [transcript]);

  const renderedTranscript = useMemo(() => {
    if (Array.isArray(transcript)) {
      if (transcript.length === 0) {
        return <div style={{ color: "#64748b" }}>No transcript available</div>;
      }

      return transcript.map((msg: any, idx: number) => {
        const isAgent = Boolean(msg?.is_agent);
        const speaker = isAgent ? "Agent" : "You";
        const timestampSec = getTranscriptTimestampSeconds(
          msg,
          transcriptBaseFirstReceivedTime
        );
        const rowClass = isAgent ? "agent" : "user";
        const stableKey =
          msg?.id ??
          msg?.start_time ??
          msg?.first_received_time ??
          `${speaker}-${idx}`;

        return (
          <div key={stableKey} className={`irv2-bubbleRow ${rowClass}`}>
            <div className="irv2-bubbleRow__meta">
              <span
                style={{
                  fontWeight: 800,
                  color: isAgent ? "var(--primary-color, #2563eb)" : "#0f172a",
                }}
              >
                {speaker}
              </span>
              <span>
                {timestampSec !== null ? formatTimestamp(timestampSec) : ""}
              </span>
            </div>
            <div className={`irv2-bubbleRow__bubble ${rowClass}`}>
              {msg?.segment_text || ""}
            </div>
          </div>
        );
      });
    }

    if (transcript) {
      return (
        <div className="irv2-bubbleRow__bubble agent">{String(transcript)}</div>
      );
    }

    return <div style={{ color: "#64748b" }}>No transcript available</div>;
  }, [transcript, transcriptBaseFirstReceivedTime]);

  const audioUrl = useMemo(() => {
    return (
      audioData?.recordingUrl ||
      audioData?.recording_url ||
      audioData?.data?.recordingUrl ||
      audioData?.data?.recording_url ||
      audioData?.url ||
      null
    );
  }, [audioData]);

  const handleJumpToCategory = (categoryName: string) => {
    setActiveTab("rubric");
    setExpandedCategory(categoryName);
    window.setTimeout(() => {
      categoryScoresRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
  };

  const persona = useMemo(() => {
    const raw = interview?.persona;
    if (raw == null) return null;
    if (typeof raw === "object") {
      return raw as { id?: string | number; name?: string; description?: string };
    }
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw) as {
          id?: string | number;
          name?: string;
          description?: string;
        };
      } catch {
        return null;
      }
    }
    return null;
  }, [interview]);

  const scoreMeta = useMemo(() => getInterviewScoreMeta(overallScorePct), [overallScorePct]);

  const gaugeStroke = useMemo(() => {
    if (overallScorePct >= 70) return "#10b981";
    if (overallScorePct >= 50) return "#f59e0b";
    return "#ef4444";
  }, [overallScorePct]);

  if (loading) {
    return (
      <div className="interview-report-v2">
        <div className="irv2-card" style={{ padding: 18 }}>
          Loading interview report...
        </div>
      </div>
    );
  }

  if (!interview) {
    return (
      <div className="interview-report-v2">
        <div className="irv2-card" style={{ padding: 18 }}>
          Interview not found.
          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              className="custom__button primary__button sm"
              onClick={() => navigate(ROUTES.INTERVIEW_LOGS)}
            >
              Back to Interview History
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="interview-report-v2">
      <div className="interview-report-v2__top">
        <button
          type="button"
          className="interview-report-v2__back interview-report-v2__back--end"
          onClick={() => navigate(ROUTES.INTERVIEW_LOGS)}
        >
          <ArrowLeft size={18} /> Back
        </button>
        <div className="interview-report-v2__topMain">
          <h1 className="interview-report-v2__title">Interview Report</h1>
          <p className="interview-report-v2__meta">
            <span>{jobRole}</span>
            <span className="dot">·</span>
            <span>{interviewDate}</span>
            <span className="dot">·</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Clock size={14} />
              {durationLabel}
            </span>
            <span className="dot">·</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <MessageSquare size={14} />
              {evaluations.length} questions
            </span>
          </p>
        </div>
      </div>

      <div className="irv2-card irv2-overall">
        <div className="irv2-overall__label">Overall Score</div>
        <div className="irv2-overall__row">
          <div className="irv2-overall__gauge">
            <div className="irv2-overall__gaugeWrap">
              <svg
                viewBox="0 0 120 120"
                className="w-full h-full"
                style={{ transform: "rotate(-90deg)" }}
              >
                <circle
                  cx="60"
                  cy="60"
                  r="52"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="8"
                />
                <circle
                  cx="60"
                  cy="60"
                  r="52"
                  fill="none"
                  stroke={gaugeStroke}
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${(overallScorePct / 100) * 2 * Math.PI * 52} ${2 * Math.PI * 52}`}
                />
              </svg>
              <div className="irv2-overall__score">{overallScorePct}%</div>
            </div>
            <div
              className={`irv2-overall__badge irv2-overall__badge--${scoreMeta.band}`}
            >
              {scoreMeta.label}
            </div>
          </div>

          <div className="irv2-statsGrid">
            <button
              type="button"
              className={`irv2-miniStat ${bestArea ? "clickable" : ""}`}
              onClick={() =>
                bestArea ? handleJumpToCategory(bestArea.name) : undefined
              }
              disabled={!bestArea}
            >
              <div className="irv2-miniStat__label">
                <Award size={14} color="#d97706" />
                Best Area
              </div>
              <p className="irv2-miniStat__value">{bestArea?.name || "—"}</p>
              <div className="irv2-miniStat__sub">
                {bestArea ? `${bestArea.score}/${bestArea.maxScore}` : ""}
              </div>
            </button>

            <button
              type="button"
              className={`irv2-miniStat ${focusArea ? "clickable" : ""}`}
              onClick={() =>
                focusArea ? handleJumpToCategory(focusArea.name) : undefined
              }
              disabled={!focusArea}
            >
              <div className="irv2-miniStat__label">
                <Target size={14} color="#d97706" />
                Focus Area
              </div>
              <p className="irv2-miniStat__value">{focusArea?.name || "—"}</p>
              <div className="irv2-miniStat__sub">
                {focusArea ? `${focusArea.score}/${focusArea.maxScore}` : ""}
              </div>
            </button>

            <div className="irv2-miniStat">
              <div className="irv2-miniStat__label">
                <Clock size={14} color="var(--primary-color, #2563eb)" />
                Duration
              </div>
              <p className="irv2-miniStat__value">{durationLabel}</p>
              <div className="irv2-miniStat__sub">Minutes</div>
            </div>

            <button
              type="button"
              className="irv2-miniStat clickable"
              onClick={() => setActiveTab("questions")}
            >
              <div className="irv2-miniStat__label">
                <BarChart2 size={14} color="var(--primary-color, #2563eb)" />
                Questions
              </div>
              <p className="irv2-miniStat__value">{evaluations.length} asked</p>
              <div className="irv2-miniStat__sub">
                {evaluations.length} answered
              </div>
            </button>
          </div>
        </div>

        {persona ? (
          <div className="irv2-personaBar">
            <div className="irv2-personaBar__icon">
              <Mic size={18} />
            </div>
            <div className="irv2-personaBar__text">
              <p className="irv2-personaBar__title">
                AI Interviewer: {persona.name || "Interviewer"}
              </p>
              <p className="irv2-personaBar__desc">
                {persona.description || "No description available."}
              </p>
            </div>
          </div>
        ) : null}
      </div>

      <div className="interview-report-v2__tabs">
        <button
          type="button"
          className={`interview-report-v2__tab ${activeTab === "rubric" ? "active" : ""}`}
          onClick={() => setActiveTab("rubric")}
        >
          Interview Rubric
        </button>
        <button
          type="button"
          className={`interview-report-v2__tab ${activeTab === "questions" ? "active" : ""}`}
          onClick={() => setActiveTab("questions")}
        >
          Question Analysis
        </button>
        <button
          type="button"
          className={`interview-report-v2__tab ${activeTab === "transcript" ? "active" : ""}`}
          onClick={() => setActiveTab("transcript")}
        >
          Transcript
        </button>
      </div>

      {activeTab === "rubric" ? (
        <div>
          <div className="irv2-card irv2-radar">
            <h2 className="irv2-sectionTitle">Performance Overview</h2>
            <p className="irv2-sectionSub">Hover over each axis to see your score</p>
            <div style={{ marginTop: 12, height: 280 }}>
              <ResponsiveContainerC width="100%" height="100%">
                <RadarChartC
                  data={metrics.map((m) => ({
                    category: m.name,
                    score: Math.max(0, Math.min(100, m.percentScore)),
                    fullMark: 100,
                  }))}
                >
                  <PolarGridC stroke="rgba(148, 163, 184, 0.22)" />
                  <PolarAngleAxisC
                    dataKey="category"
                    tick={{
                      fill: "var(--primary-text-color, #cbd5e1)",
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  />
                  <PolarRadiusAxisC domain={[0, 100]} tick={false} axisLine={false} />
                  <RadarC
                    name="Score"
                    dataKey="score"
                    stroke="var(--irv2-radar-blue, #3b82f6)"
                    fill="var(--irv2-radar-blue, #3b82f6)"
                    fillOpacity={0.38}
                    strokeWidth={2}
                  />
                  <TooltipC
                    content={<RadarTooltip />}
                    cursor={{
                      stroke: "var(--irv2-radar-cursor, rgba(148, 163, 184, 0.28))",
                      strokeWidth: 1,
                    }}
                  />
                </RadarChartC>
              </ResponsiveContainerC>
            </div>
          </div>

          <div ref={categoryScoresRef}>
            <div className="irv2-categoryGrid">
              {metrics.map((m) => {
                const active = expandedCategory === m.name;
                const ringColor = getCategoryRingColor(m.score, m.maxScore);
                const pct = Math.max(
                  0,
                  Math.min(100, (m.score / (m.maxScore || 10)) * 100)
                );
                const dash = `${(pct / 100) * 2 * Math.PI * 13} ${2 * Math.PI * 13}`;
                return (
                  <button
                    key={m.id}
                    type="button"
                    className={`irv2-categoryCard ${active ? "active" : ""}`}
                    onClick={() => setExpandedCategory(m.name)}
                  >
                    <div className="irv2-categoryCard__ringWrap">
                      <svg viewBox="0 0 32 32" className="irv2-categoryCard__ringSvg">
                        <circle
                          cx="16"
                          cy="16"
                          r="13"
                          fill="none"
                          stroke="var(--primary-border-color, #e5e7eb)"
                          strokeWidth="2.5"
                        />
                        <circle
                          cx="16"
                          cy="16"
                          r="13"
                          fill="none"
                          stroke={ringColor}
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeDasharray={dash}
                          className="irv2-categoryCard__ringArc"
                        />
                      </svg>
                      <div className="irv2-categoryCard__ringIcon">
                        <CheckCircle size={12} style={{ color: ringColor }} />
                      </div>
                    </div>
                    <div className="irv2-categoryCard__score">
                      <span
                        className={`irv2-categoryCard__scoreNum ${getScoreBandClass(
                          m.score,
                          m.maxScore
                        )}`}
                      >
                        {m.score}
                      </span>
                      <span className="irv2-categoryCard__scoreDenom">/{m.maxScore}</span>
                    </div>
                    <div className="irv2-categoryCard__name">{m.name}</div>
                  </button>
                );
              })}
            </div>
            <div style={{ textAlign: "center", marginTop: 8, fontSize: 12, color: "#64748b" }}>
              Click Category for More Details
            </div>
          </div>

          {expandedCategory ? (
            (() => {
              const cat = metrics.find((m) => m.name === expandedCategory);
              if (!cat) return null;
              return (
                <div className="irv2-card irv2-expand">
                  <div className="irv2-expand__header">
                    <div>
                      <h2 className="irv2-sectionTitle">Feedback</h2>
                      <div className="irv2-expand__desc">{cat.description || "—"}</div>
                    </div>
                    <div className="irv2-expand__scoreBlock">
                      <div className="irv2-expand__scorePair">
                        <span
                          className={`irv2-expand__scoreNum ${getScoreBandClass(
                            cat.score,
                            cat.maxScore
                          )}`}
                        >
                          {cat.score}
                        </span>
                        <span className="irv2-expand__scoreDenom">/{cat.maxScore}</span>
                      </div>
                      {cat.rating ? (
                        <div style={{ marginTop: 6, fontSize: 12, fontWeight: 800, color: "#64748b" }}>
                          {cat.rating}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="irv2-expand__cols">
                    <div className="irv2-expand__col irv2-expand__col--improve">
                      <p className="irv2-expand__colTitle irv2-expand__colTitle--amber">
                        How to Improve
                      </p>
                      <div className="irv2-expand__box irv2-expand__box--amber">
                        {cat.suggestion || "—"}
                      </div>
                    </div>
                    <div className="irv2-expand__col irv2-expand__col--example">
                      <p className="irv2-expand__colTitle irv2-expand__colTitle--green">
                        Strong Answer Example
                      </p>
                      <div className="irv2-expand__box irv2-expand__box--green">
                        {cat.example ? `"${cat.example}"` : "—"}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()
          ) : null}
        </div>
      ) : null}

      {activeTab === "questions" ? (
        <div className="irv2-questions">
          <div className="irv2-card irv2-qList">
            {evaluations.length === 0 ? (
              <div style={{ color: "#64748b" }}>No question analysis available.</div>
            ) : (
              evaluations.map((q: any, idx: number) => {
                const id = q?.id ?? idx;
                const isActive = (selectedQuestionId ?? "") === id;
                return (
                  <button
                    key={id}
                    type="button"
                    className={`irv2-qItem ${isActive ? "active" : ""}`}
                    onClick={() => setSelectedQuestionId(id)}
                  >
                    <div className="irv2-qItem__qno">Q{idx + 1}</div>
                    <div className="irv2-qItem__text">{q?.question || "—"}</div>
                  </button>
                );
              })
            )}
          </div>

          <div className="irv2-card irv2-qDetail">
            {selectedQuestion ? (
              <>
                <div className="irv2-qDetail__head">
                  <h3 className="irv2-qDetail__question">{selectedQuestion.question}</h3>
                  <div className="irv2-qDetail__chips">
                    <div className="irv2-qDetail__chip irv2-qDetail__chip--tech">
                      Technical: {selectedQuestion.technicalScore}/10
                    </div>
                    <div className="irv2-qDetail__chip irv2-qDetail__chip--comm">
                      Communication: {selectedQuestion.communicationScore}/10
                    </div>
                  </div>
                </div>

                <div className="irv2-qDetail__body">
                  <div>
                    <p className="irv2-qDetail__blockLabel">Your Answer</p>
                    <div className="irv2-qDetail__answerBox">
                      {selectedQuestion.answer || "—"}
                    </div>
                  </div>

                  <div className="irv2-qDetail__cols3">
                    <div className="irv2-qDetail__listBox irv2-qDetail__listBox--strong">
                      <p className="irv2-qDetail__listTitle" style={{ color: "#16a34a" }}>
                        Strengths
                      </p>
                      <ul className="irv2-qDetail__ul">
                        {(selectedQuestion.strengths || []).map((s: string) => (
                          <li key={`${selectedQuestion.id ?? "q"}-${s}`}>{s}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="irv2-qDetail__listBox irv2-qDetail__listBox--improving">
                      <p className="irv2-qDetail__listTitle" style={{ color: "#d97706" }}>
                        Areas for Improvement
                      </p>
                      <ul className="irv2-qDetail__ul">
                        {(selectedQuestion.areasOfImprovement || []).map((s: string) => (
                          <li key={`${selectedQuestion.id ?? "q"}-${s}`}>{s}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="irv2-qDetail__listBox irv2-qDetail__listBox--better">
                      <p className="irv2-qDetail__listTitle" style={{ color: "#2563eb" }}>
                        What Can Be Done Better
                      </p>
                      <ul className="irv2-qDetail__ul">
                        {(selectedQuestion.whatCanBeDoneBetter || []).map((s: string) => (
                          <li key={`${selectedQuestion.id ?? "q"}-${s}`}>{s}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {selectedQuestion.overallFeedback ? (
                    <div>
                      <p className="irv2-qDetail__blockLabel">Overall Feedback</p>
                      <div className="irv2-qDetail__answerBox">
                        {selectedQuestion.overallFeedback}
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="irv2-qDetail__foot">
                  <button
                    type="button"
                    className="custom__button tac_primary__button sm irv2-qDetail__footBtn"
                    disabled={selectedQuestionIndex <= 0}
                    onClick={() => {
                      if (selectedQuestionIndex > 0) {
                        const prev = evaluations[selectedQuestionIndex - 1];
                        setSelectedQuestionId(prev?.id ?? selectedQuestionIndex - 1);
                      }
                    }}
                  >
                    Previous
                  </button>
                  <span className="irv2-qDetail__footMid">
                    {selectedQuestionIndex >= 0 ? `${selectedQuestionIndex + 1} / ${evaluations.length}` : ""}
                  </span>
                  <button
                    type="button"
                    className="custom__button primary__button sm irv2-qDetail__footBtn irv2-qDetail__footBtn--next"
                    disabled={selectedQuestionIndex < 0 || selectedQuestionIndex >= evaluations.length - 1}
                    onClick={() => {
                      if (selectedQuestionIndex >= 0 && selectedQuestionIndex < evaluations.length - 1) {
                        const next = evaluations[selectedQuestionIndex + 1];
                        setSelectedQuestionId(next?.id ?? selectedQuestionIndex + 1);
                      }
                    }}
                  >
                    Next
                  </button>
                </div>
              </>
            ) : (
              <div style={{ padding: 18, color: "#64748b" }}>
                Select a question to view details.
              </div>
            )}
          </div>
        </div>
      ) : null}

      {activeTab === "transcript" ? (
        <div className="irv2-card">
          <div className="irv2-audio">
            <h2 className="irv2-sectionTitle">Conversation Transcript</h2>
            <p className="irv2-sectionSub">Audio + transcript (when available)</p>
            <div style={{ marginTop: 12 }}>
              {audioUrl ? (
                <audio controls style={{ width: "100%" }} src={audioUrl}>
                  <track kind="captions" />
                </audio>
              ) : (
                <div style={{ color: "#64748b" }}>No audio recording available</div>
              )}
            </div>
          </div>
          <div className="irv2-transcript">{renderedTranscript}</div>
        </div>
      ) : null}
    </div>
  );
};

export default InterviewLogDetailReportV2;

